import { createContext, useContext, type ReactNode } from "react";
import { useAgentSession } from "./useAgentSession";

type AgentSessionValue = ReturnType<typeof useAgentSession>;

const AgentSessionContext = createContext<AgentSessionValue | null>(null);

/**
 * Provides a single useAgentSession() instance to the subtree so that
 * CenterPane (streaming / new thread) and LeftPane (chat switch) share the
 * same hook refs and module-scoped timers. Without a shared instance,
 * separate renders of useAgentSession() would hold distinct sessionIdRefs
 * and the chat-switch teardown could not affect the streaming reader
 * owned by CenterPane. See issue #60.
 */
export function AgentSessionProvider({ children }: { children: ReactNode }) {
	const value = useAgentSession();
	return <AgentSessionContext.Provider value={value}>{children}</AgentSessionContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- context and its hook live together intentionally
export function useAgentSessionContext(): AgentSessionValue {
	const ctx = useContext(AgentSessionContext);
	if (!ctx) {
		throw new Error("useAgentSessionContext must be used within <AgentSessionProvider>");
	}
	return ctx;
}
